import { aiFilterSearchRequest } from '../CustomDash/filters/ai-search/ai-search-api-request';
import { screenerAiSearchRequest } from '../CustomDash/filters/ai-search/screener-ai-search-api-request';
import type {
  GlobalAssistantResolvedRouteFilters,
  GlobalAssistantRouteFilterKind,
} from './global-ai-assistant-route-filter-kind';
import type { AssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

/**
 * Eager route-filter resolution cache.
 *
 * Two tiers:
 * 1. In-memory promise cache — dedupes in-flight requests within a tab.
 * 2. localStorage cache — persists RESOLVED responses so repeat visits /
 *    clicks on hydrated-session chips don't re-hit the API.
 *
 * Cache key includes `filterKind` so Talent Discovery v2 and screener
 * responses never collide for the same prompt.
 *
 * Taxonomy (TD only) is part of the key because the AI filter endpoint's
 * response depends on role/skill taxonomy; screener uses prompt-only keys.
 *
 * Bump `CACHE_VERSION` when the stored envelope or semantics change.
 */

/**
 * Bump this when the cached shape could misapply:
 *
 * 1. Upstream response shape changes for TD or screener apply paths.
 * 2. The prompt / taxonomy contract to either endpoint changes materially.
 * 3. This module's own stored envelope evolves.
 */
const CACHE_VERSION = '2026-04-24-1';

const STORAGE_KEY = 'revelio.globalAiAssistant.routeFilterCache.v2';
const MAX_ENTRIES = 30;

type FilterPromise = Promise<GlobalAssistantResolvedRouteFilters>;

type StoredEntry = {
  key: string;
  resolved: GlobalAssistantResolvedRouteFilters;
  cacheVersion: string;
};

function buildKey(
  prompt: string,
  tax: AssistantTaxonomy,
  filterKind: GlobalAssistantRouteFilterKind
): string {
  if (filterKind === 'screener') {
    return `${prompt}\u0001screener`;
  }
  return `${prompt}\u0001${tax.rolesKey}\u0001${tax.skillsKey}\u0001tdV2`;
}

const inFlight = new Map<string, FilterPromise>();

function readStore(): StoredEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is StoredEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as StoredEntry).key === 'string' &&
        typeof (e as StoredEntry).cacheVersion === 'string' &&
        (e as StoredEntry).cacheVersion === CACHE_VERSION &&
        (e as StoredEntry).resolved !== undefined
    );
  } catch {
    return [];
  }
}

function writeStore(entries: StoredEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota errors / private browsing: non-fatal.
  }
}

function readStoredFresh(
  key: string
): GlobalAssistantResolvedRouteFilters | null {
  const entry = readStore().find((e) => e.key === key);
  return entry?.resolved ?? null;
}

function writeStored(
  key: string,
  resolved: GlobalAssistantResolvedRouteFilters
): void {
  const entries = readStore().filter((e) => e.key !== key);
  entries.push({ key, resolved, cacheVersion: CACHE_VERSION });
  const overflow = entries.length - MAX_ENTRIES;
  writeStore(overflow > 0 ? entries.slice(overflow) : entries);
}

type FetchOptions = {
  bypassCache?: boolean;
  filterKind?: GlobalAssistantRouteFilterKind;
  /**
   * Screener cache partition. Company/geography/role Discovery currently share
   * one endpoint contract, but the cache key keeps entity scope explicit so a
   * future endpoint split cannot silently serve cross-entity stale responses.
   */
  screenerEntityType?: 'company' | 'geography' | 'role';
};

function resolveFilterKind(
  options: FetchOptions | undefined
): GlobalAssistantRouteFilterKind {
  return options?.filterKind ?? 'tdV2';
}

function resolveScreenerEntityType(
  options: FetchOptions | undefined
): 'company' | 'geography' | 'role' | 'unknown' {
  return options?.screenerEntityType ?? 'unknown';
}

/**
 * Start resolving filters for `prompt` in the background.
 * Returns a (possibly already-running or already-resolved) promise.
 */
export function prefetchRouteFilters(
  prompt: string,
  taxonomy: AssistantTaxonomy,
  options?: FetchOptions
): FilterPromise {
  const normalized = prompt.trim();
  if (normalized.length === 0) {
    return Promise.reject(new Error('Empty prompt'));
  }
  const filterKind = resolveFilterKind(options);
  const key =
    filterKind === 'screener'
      ? `${normalized}\u0001screener\u0001${resolveScreenerEntityType(options)}`
      : buildKey(normalized, taxonomy, filterKind);

  if (!options?.bypassCache) {
    const existing = inFlight.get(key);
    if (existing) return existing;

    const stored = readStoredFresh(key);
    if (stored !== null) {
      const resolved = Promise.resolve(stored);
      inFlight.set(key, resolved);
      return resolved;
    }
  }

  const promise = (() => {
    if (filterKind === 'screener') {
      return screenerAiSearchRequest({ prompt: normalized }).then(
        ({ response }) => {
          const payload: GlobalAssistantResolvedRouteFilters = {
            filterKind: 'screener',
            response,
          };
          writeStored(key, payload);
          return payload;
        }
      );
    }
    return aiFilterSearchRequest({
      prompt: normalized,
      rolesTax: taxonomy.rolesTax,
      skillsTax: taxonomy.skillsTax,
    }).then(({ response }) => {
      const payload: GlobalAssistantResolvedRouteFilters = {
        filterKind: 'tdV2',
        response,
      };
      writeStored(key, payload);
      return payload;
    });
  })().finally(() => {
    if (inFlight.get(key) === promise) {
      inFlight.delete(key);
    }
  });

  inFlight.set(key, promise);
  return promise;
}

/** Alias — semantically "get from cache, fallback to fetch". */
export function getRouteFilterResponse(
  prompt: string,
  taxonomy: AssistantTaxonomy,
  options?: FetchOptions
): FilterPromise {
  return prefetchRouteFilters(prompt, taxonomy, options);
}

/** Test hook — reset both tiers. */
export function __resetRouteFilterPrefetchCacheForTests(): void {
  inFlight.clear();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
